import {
    createContext,
    type ReactNode,
    useContext,
    useEffect,
    Fragment,
    useMemo,
    type FC
} from "react";
import {
    Text as NativeText,
    Platform,
    View
} from "react-native";
import type IMarkdownViewerProps from "./type";
import type {
    MarkdownListGroupItemType,
    MarkdownInlineContextType,
    MarkdownBlockContextType,
    MarkdownListGroupType,
    MarkdownInlineObject,
    TextMarkdownTypes,
    MarkdownObject
} from "./type";
import stylesheet, {
    getMarkdownQuoteIconContainerStyle,
    getMarkdownReadingInlineStyle,
    getMarkdownDecorationStyle,
    getMarkdownInlineCodeStyle,
    getMarkdownQuoteIconSize,
    getMarkdownHeadingStyle,
    getMarkdownSpacerStyle,
    getMarkdownListMarker,
    getMarkdownVariant,
    useStyles
} from "./stylesheet";
import {
    MARKDOWN_HEADING_KEYS,
    READING_VARIANTS
} from "./constant";
import MarkdownImage from "./internal/image";
import MarkdownLink from "./internal/link";
import MarkdownLinkBlock from "./internal/linkBlock";
import {
    getMarkdownListGroups,
    parseMarkdownInline,
    getMarkdownSafeUrl,
    parseMarkdown
} from "./util";
import {
    NCoreUIKitLocalize,
    NCoreUIKitTheme
} from "../../core/hooks";
import type ITextProps from "../text/type";
import {
    Quote as QuoteIcon
} from "lucide-react-native";
import {
    isReadingVariant,
    isArabicLocale
} from "../../helpers/theme/localeTypography";
import {
    warnOnce
} from "../../utils/development";
import CodeViewer from "../codeViewer";
import Separator from "../separator";
import Text from "../text";

const MarkdownLinkScopeContext = createContext<boolean>(false);

const MarkdownViewer: FC<IMarkdownViewerProps> = ({
    blockquoteTextColor = "high",
    blockquoteIconColor = "mid",
    isShowImageCaption = true,
    blockquoteContainerProps,
    centerContainerProps,
    blockquoteIconStyle,
    blockquoteTextStyle,
    centerMarkdownProps,
    rightContainerProps,
    syntax = "standard",
    blockquoteIconSize,
    codeContainerProps,
    leftContainerProps,
    linkContainerProps,
    listContainerProps,
    rightMarkdownProps,
    isReading = false,
    leftMarkdownProps,
    linkMarkdownProps,
    customTextStyles,
    customTextProps,
    italicTextProps,
    customLocalize,
    enterTextProps,
    boldTextProps,
    listIconProps,
    listTextProps,
    customTheme,
    italicStyle,
    onLinkPress,
    imageProps,
    boldStyle,
    variants,
    content,
    style,
    ...props
}) => {
    const {
        shapeConfigs,
        typography,
        iconSizes,
        radiuses,
        borders,
        colors,
        spaces
    } = NCoreUIKitTheme.useContext(customTheme, customLocalize);

    const {
        activeLocale
    } = NCoreUIKitLocalize.useContext(customLocalize);

    const isInsideLink = useContext(MarkdownLinkScopeContext);

    const currentVariants = useMemo(() => isReading ? {
        ...READING_VARIANTS,
        ...variants
    } : variants, [
        isReading,
        variants
    ]);

    const isArabic = isArabicLocale({
        locale: activeLocale
    });

    const textLocalize = isReading ? customLocalize : undefined;

    const nodes = useMemo(() => parseMarkdown(content, syntax), [
        content,
        syntax
    ]);

    const {
        blockquoteContainer: blockquoteContainerDynamicStyle,
        blockquoteContent: blockquoteContentDynamicStyle,
        listContent: listContentDynamicStyle,
        listMarker: listMarkerDynamicStyle,
        container: containerDynamicStyle,
        strong: strongDynamicStyle,
        list: listDynamicStyle
    } = useStyles({
        shapeConfigs,
        typography,
        isReading,
        radiuses,
        colors,
        spaces,
        syntax
    });

    useEffect(() => {
        const deprecatedProps: Record<string, unknown> = {
            centerContainerProps,
            centerMarkdownProps,
            rightContainerProps,
            leftContainerProps,
            linkContainerProps,
            rightMarkdownProps,
            leftMarkdownProps,
            linkMarkdownProps
        };

        Object.keys(deprecatedProps).forEach((propName) => {
            if(deprecatedProps[propName] === undefined) {
                return;
            }

            warnOnce({
                message: `MarkdownViewer: "${propName}" is deprecated and will be removed in the next major version. It only applies to syntax="legacy" blocks.`
            });
        });
    }, [
        centerContainerProps,
        rightContainerProps,
        centerMarkdownProps,
        leftContainerProps,
        linkContainerProps,
        rightMarkdownProps,
        leftMarkdownProps,
        linkMarkdownProps
    ]);

    const rootContext: MarkdownBlockContextType = {
        textColor: isInsideLink ? "link" : "high",
        isUnderline: isInsideLink
    };

    const getInlineContext = ({
        keyPrefix,
        context,
        variant
    }: {
        variant: keyof NCoreUIKit.Typography;
        context: MarkdownBlockContextType;
        keyPrefix: string;
    }): MarkdownInlineContextType => {
        return {
            fontSize: typography[variant].fontSize,
            isUnderline: context.isUnderline,
            isStrikethrough: false,
            isEmphasis: false,
            isStrong: false,
            isInsideLink,
            keyPrefix,
            variant
        };
    };

    const renderInlineLink = ({
        context,
        inline
    }: {
        context: MarkdownInlineContextType;
        inline: MarkdownInlineObject;
    }) => {
        const isImage = inline.type === "image";

        const safeUrl = getMarkdownSafeUrl({
            url: inline.url,
            isImage
        });

        const linkChildren: Array<MarkdownInlineObject> = isImage || !inline.children?.length ? [
            {
                content: inline.content || inline.url || "",
                type: "text"
            }
        ] : inline.children;

        if(!safeUrl || context.isInsideLink) {
            return <Fragment
                key={context.keyPrefix}
            >
                {renderInlines(linkChildren, context)}
            </Fragment>;
        }

        return <MarkdownLink
            style={getMarkdownDecorationStyle({
                isStrikethrough: context.isStrikethrough,
                isUnderline: true
            })}
            onLinkPress={onLinkPress}
            key={context.keyPrefix}
            radiuses={radiuses}
            borders={borders}
            colors={colors}
            url={safeUrl}
        >
            {renderInlines(linkChildren, {
                ...context,
                isInsideLink: true,
                isUnderline: true
            })}
        </MarkdownLink>;
    };

    const renderInline = ({
        context,
        inline
    }: {
        context: MarkdownInlineContextType;
        inline: MarkdownInlineObject;
    }): ReactNode => {
        const children = inline.children ?? [];

        switch(inline.type) {
            case "text":
                return inline.content;
            case "break":
                return "\n";
            case "strong":
                return <NativeText
                    {...boldTextProps}
                    style={[
                        isReadingVariant(context.variant) ? getMarkdownReadingInlineStyle({
                            token: typography[context.variant],
                            isEmphasis: context.isEmphasis,
                            isStrong: true,
                            isArabic
                        }) : strongDynamicStyle,
                        boldStyle
                    ]}
                    key={context.keyPrefix}
                >
                    {renderInlines(children, {
                        ...context,
                        isStrong: true
                    })}
                </NativeText>;
            case "emphasis":
                return <NativeText
                    {...italicTextProps}
                    style={[
                        isReadingVariant(context.variant) ? getMarkdownReadingInlineStyle({
                            token: typography[context.variant],
                            isStrong: context.isStrong,
                            isEmphasis: true,
                            isArabic
                        }) : stylesheet.italic,
                        italicStyle
                    ]}
                    key={context.keyPrefix}
                >
                    {renderInlines(children, {
                        ...context,
                        isEmphasis: true
                    })}
                </NativeText>;
            case "strikethrough":
                return <NativeText
                    style={getMarkdownDecorationStyle({
                        isUnderline: context.isUnderline,
                        isStrikethrough: true
                    })}
                    key={context.keyPrefix}
                >
                    {renderInlines(children, {
                        ...context,
                        isStrikethrough: true
                    })}
                </NativeText>;
            case "underline":
                return <NativeText
                    style={getMarkdownDecorationStyle({
                        isStrikethrough: context.isStrikethrough,
                        isUnderline: true
                    })}
                    key={context.keyPrefix}
                >
                    {renderInlines(children, {
                        ...context,
                        isUnderline: true
                    })}
                </NativeText>;
            case "code":
                return <NativeText
                    style={getMarkdownInlineCodeStyle({
                        variant: getMarkdownVariant({
                            variants: currentVariants,
                            nativeType: "`"
                        }),
                        fontSize: context.fontSize,
                        typography,
                        radiuses,
                        colors,
                        spaces
                    })}
                    key={context.keyPrefix}
                >
                    {inline.content}
                </NativeText>;
            case "image":
            case "link":
                return renderInlineLink({
                    context,
                    inline
                });
            default:
                return inline.content;
        }
    };

    const renderInlines = (inlines: Array<MarkdownInlineObject>, context: MarkdownInlineContextType): Array<ReactNode> => {
        return inlines.map((inline, index) => renderInline({
            context: {
                ...context,
                keyPrefix: `${context.keyPrefix}-${index}`
            },
            inline
        }));
    };

    const getNodeInlines = (node: MarkdownObject): Array<MarkdownInlineObject> => {
        return node.inlines ?? parseMarkdownInline(node.content, syntax);
    };

    const renderText = ({
        context,
        isFirst,
        node
    }: {
        context: MarkdownBlockContextType;
        node: MarkdownObject;
        isFirst: boolean;
    }) => {
        const nativeType = node.nativeType as TextMarkdownTypes;

        const level = node.level ?? MARKDOWN_HEADING_KEYS.indexOf(nativeType) + 1;

        const isHeading = level > 0;

        const variant = !isHeading && context.textVariant ? context.textVariant : getMarkdownVariant({
            nativeType: node.nativeType,
            variants: currentVariants
        });

        let textProps: ITextProps = {
            color: context.textColor,
            variant
        };

        const currentTextStyle = customTextStyles?.[nativeType];

        if(currentTextStyle) {
            textProps.style = currentTextStyle;
        }

        const currentTextProps = customTextProps?.[nativeType];

        if(currentTextProps) {
            textProps = {
                ...textProps,
                ...currentTextProps
            };
        }

        const headingLevelProps = isHeading && Platform.OS === "web" ? {
            "aria-level": level
        } : {};

        return <Text
            {...textProps}
            {...headingLevelProps}
            style={[
                textProps.style,
                context.textStyle,
                context.isUnderline ? stylesheet.underline : undefined,
                getMarkdownHeadingStyle({
                    isHeading,
                    isFirst,
                    spaces
                })
            ]}
            role={isHeading ? "heading" : textProps.role}
            customLocalize={textLocalize}
            customTheme={customTheme}
            key={node.key}
        >
            {renderInlines(getNodeInlines(node), getInlineContext({
                variant: textProps.variant ?? variant,
                keyPrefix: `${node.key}`,
                context
            }))}
        </Text>;
    };

    const renderEnter = ({
        node
    }: {
        node: MarkdownObject
    }) => {
        return <View
            {...enterTextProps}
            style={[
                stylesheet.enter,
                getMarkdownSpacerStyle({
                    variant: getMarkdownVariant({
                        nativeType: node.nativeType,
                        variants: currentVariants
                    }),
                    typography
                }),
                enterTextProps?.style
            ]}
            key={node.key}
        />;
    };

    const renderBlockquote = ({
        context,
        node
    }: {
        context: MarkdownBlockContextType;
        node: MarkdownObject;
    }) => {
        const quoteVariant = getMarkdownVariant({
            nativeType: node.nativeType,
            variants: currentVariants
        });

        const quoteContext: MarkdownBlockContextType = {
            isUnderline: context.isUnderline,
            textColor: blockquoteTextColor,
            textStyle: blockquoteTextStyle,
            textVariant: quoteVariant
        };

        const quoteIconSize = getMarkdownQuoteIconSize({
            blockquoteIconSize,
            typography,
            iconSizes
        });

        const renderQuoteContent = () => {
            if(node.children) {
                return <View
                    style={[
                        stylesheet.blockquoteContent,
                        blockquoteContentDynamicStyle
                    ]}
                >
                    {renderBlocks({
                        blockNodes: node.children,
                        context: quoteContext
                    })}
                </View>;
            }

            return <Text
                style={[
                    stylesheet.blockquoteText,
                    blockquoteTextStyle
                ]}
                customLocalize={textLocalize}
                color={blockquoteTextColor}
                customTheme={customTheme}
                variant={quoteVariant}
            >
                {renderInlines(getNodeInlines(node), getInlineContext({
                    keyPrefix: `${node.key}`,
                    variant: quoteVariant,
                    context
                }))}
            </Text>;
        };

        return <View
            {...blockquoteContainerProps}
            style={[
                stylesheet.blockquoteContainer,
                blockquoteContainerDynamicStyle,
                blockquoteContainerProps?.style
            ]}
            key={node.key}
        >
            <View
                style={[
                    stylesheet.blockquoteIconContainer,
                    getMarkdownQuoteIconContainerStyle({
                        iconSize: quoteIconSize,
                        variant: quoteVariant,
                        typography
                    })
                ]}
                aria-hidden={true}
            >
                <QuoteIcon
                    color={colors.content.icon[blockquoteIconColor]}
                    style={[
                        stylesheet.blockquoteIcon,
                        blockquoteIconStyle
                    ]}
                    size={quoteIconSize}
                />
            </View>
            {renderQuoteContent()}
        </View>;
    };

    const renderImageComponent = ({
        node
    }: {
        node: MarkdownObject
    }) => {
        return <MarkdownImage
            isShowCaption={syntax === "standard" && isShowImageCaption}
            shapeConfigs={shapeConfigs}
            customTheme={customTheme}
            imageProps={imageProps}
            radiuses={radiuses}
            colors={colors}
            spaces={spaces}
            key={node.key}
            node={node}
        />;
    };

    const renderListItem = ({
        context,
        item
    }: {
        context: MarkdownBlockContextType;
        item: MarkdownListGroupItemType;
    }): ReactNode => {
        const node = item.node;

        const variant = context.textVariant ?? getMarkdownVariant({
            nativeType: node.nativeType,
            variants: currentVariants
        });

        return <View
            {...listContainerProps}
            style={[
                stylesheet.listItem,
                listContainerProps?.style
            ]}
            role="listitem"
            key={node.key}
        >
            <View
                style={[
                    stylesheet.listMarker,
                    listMarkerDynamicStyle
                ]}
            >
                <Text
                    {...listIconProps}
                    color={listIconProps?.color ?? context.textColor}
                    aria-hidden={!node.isOrdered}
                    style={[
                        listIconProps?.style,
                        stylesheet.listMarkerText
                    ]}
                    customLocalize={textLocalize}
                    customTheme={customTheme}
                    variant={variant}
                >
                    {getMarkdownListMarker({
                        node
                    })}
                </Text>
            </View>
            <View
                style={[
                    stylesheet.listContent,
                    listContentDynamicStyle
                ]}
            >
                <Text
                    {...listTextProps}
                    style={[
                        listTextProps?.style,
                        context.textStyle,
                        context.isUnderline ? stylesheet.underline : undefined
                    ]}
                    color={listTextProps?.color ?? context.textColor}
                    customLocalize={textLocalize}
                    customTheme={customTheme}
                    variant={variant}
                >
                    {renderInlines(getNodeInlines(node), getInlineContext({
                        keyPrefix: `${node.key}`,
                        context,
                        variant
                    }))}
                </Text>
                {item.groups.map((group) => renderListGroup({
                    context,
                    group
                }))}
            </View>
        </View>;
    };

    const renderListGroup = ({
        context,
        group
    }: {
        context: MarkdownBlockContextType;
        group: MarkdownListGroupType;
    }): ReactNode => {
        const firstNode = group.items[0]?.node;

        return <View
            key={`list-${firstNode ? firstNode.key : group.level}`}
            style={[
                stylesheet.list,
                listDynamicStyle
            ]}
            role="list"
        >
            {group.items.map((item) => renderListItem({
                context,
                item
            }))}
        </View>;
    };

    const renderCode = ({
        node
    }: {
        node: MarkdownObject
    }) => {
        return <View
            {...codeContainerProps}
            style={codeContainerProps?.style}
            key={node.key}
        >
            <CodeViewer
                customLocalize={customLocalize}
                borderRadius={radiuses.form}
                isLineNumberVisible={false}
                customTheme={customTheme}
                language={node.language}
                isCollapsible={false}
                code={node.content}
            />
        </View>;
    };

    const renderRule = ({
        node
    }: {
        node: MarkdownObject
    }) => {
        return <Separator
            customTheme={customTheme}
            key={node.key}
        />;
    };

    const renderNestedViewer = ({
        markdownProps,
        nestedContent
    }: {
        markdownProps?: IMarkdownViewerProps;
        nestedContent: string;
    }) => {
        return <MarkdownViewer
            {...markdownProps}
            isShowImageCaption={markdownProps?.isShowImageCaption ?? isShowImageCaption}
            customTextStyles={markdownProps?.customTextStyles ?? customTextStyles}
            customTextProps={markdownProps?.customTextProps ?? customTextProps}
            customLocalize={markdownProps?.customLocalize ?? customLocalize}
            onLinkPress={markdownProps?.onLinkPress ?? onLinkPress}
            customTheme={markdownProps?.customTheme ?? customTheme}
            isReading={markdownProps?.isReading ?? isReading}
            variants={markdownProps?.variants ?? variants}
            syntax={markdownProps?.syntax ?? syntax}
            content={nestedContent}
        />;
    };

    const renderLink = ({
        node
    }: {
        node: MarkdownObject
    }) => {
        const safeUrl = getMarkdownSafeUrl({
            url: node.url
        });

        const nestedViewer = renderNestedViewer({
            markdownProps: linkMarkdownProps,
            nestedContent: node.content
        });

        if(!safeUrl) {
            return <View
                key={node.key}
            >
                {nestedViewer}
            </View>;
        }

        return <MarkdownLinkBlock
            linkContainerProps={linkContainerProps}
            shapeConfigs={shapeConfigs}
            onLinkPress={onLinkPress}
            customTheme={customTheme}
            radiuses={radiuses}
            borders={borders}
            colors={colors}
            key={node.key}
            url={safeUrl}
        >
            <MarkdownLinkScopeContext.Provider
                value={true}
            >
                {nestedViewer}
            </MarkdownLinkScopeContext.Provider>
        </MarkdownLinkBlock>;
    };

    const renderCenter = ({
        node
    }: {
        node: MarkdownObject
    }) => {
        return <View
            {...centerContainerProps}
            style={[
                stylesheet.centerContainer,
                centerContainerProps?.style
            ]}
            key={node.key}
        >
            {renderNestedViewer({
                markdownProps: centerMarkdownProps,
                nestedContent: node.content
            })}
        </View>;
    };

    const renderLeft = ({
        node
    }: {
        node: MarkdownObject
    }) => {
        return <View
            {...leftContainerProps}
            style={[
                stylesheet.leftContainer,
                leftContainerProps?.style
            ]}
            key={node.key}
        >
            {renderNestedViewer({
                markdownProps: leftMarkdownProps,
                nestedContent: node.content
            })}
        </View>;
    };

    const renderRight = ({
        node
    }: {
        node: MarkdownObject
    }) => {
        return <View
            {...rightContainerProps}
            style={[
                stylesheet.rightContainer,
                rightContainerProps?.style
            ]}
            key={node.key}
        >
            {renderNestedViewer({
                markdownProps: rightMarkdownProps,
                nestedContent: node.content
            })}
        </View>;
    };

    const renderBlock = ({
        context,
        isFirst,
        node
    }: {
        context: MarkdownBlockContextType;
        node: MarkdownObject;
        isFirst: boolean;
    }): ReactNode => {
        if(!node.content.trim() && node.nativeType === "<p>") {
            return null;
        }

        switch(node.type) {
            case undefined:
                return null;
            case "text":
                return renderText({
                    context,
                    isFirst,
                    node
                });
            case "space":
                return renderEnter({
                    node
                });
            case "blockquote":
                return renderBlockquote({
                    context,
                    node
                });
            case "right":
                return renderRight({
                    node
                });
            case "left":
                return renderLeft({
                    node
                });
            case "center":
                return renderCenter({
                    node
                });
            case "link":
                return renderLink({
                    node
                });
            case "code":
                return renderCode({
                    node
                });
            case "image":
                return renderImageComponent({
                    node
                });
            case "rule":
                return renderRule({
                    node
                });
            default:
                return renderText({
                    context,
                    isFirst,
                    node
                });
        }
    };

    const renderBlocks = ({
        blockNodes,
        context
    }: {
        context: MarkdownBlockContextType;
        blockNodes: Array<MarkdownObject>;
    }): Array<ReactNode> => {
        const elements: Array<ReactNode> = [];

        const listItems: Array<MarkdownObject> = [];

        blockNodes.forEach((node, index) => {
            if(node.type === "list") {
                listItems.push(node);

                if(blockNodes[index + 1]?.type === "list") {
                    return;
                }

                getMarkdownListGroups(listItems).forEach((group) => {
                    elements.push(renderListGroup({
                        context,
                        group
                    }));
                });

                listItems.length = 0;

                return;
            }

            const element = renderBlock({
                isFirst: elements.length === 0,
                context,
                node
            });

            if(element) {
                elements.push(element);
            }
        });

        return elements;
    };

    return <View
        {...props}
        style={[
            stylesheet.container,
            containerDynamicStyle,
            style
        ]}
    >
        {renderBlocks({
            context: rootContext,
            blockNodes: nodes
        })}
    </View>;
};
export default MarkdownViewer;
